import { Button, DatePicker, Form } from "antd";
import React, { useMemo } from "react";
import AgTable from "./AgTable";
import "styled-components/macro";
import { useMeData } from "../hooks/useMe";
import { useAppMode, useFullScreenModeEnabled } from "../store/model-misc";
import { Callout, Spinner } from "@blueprintjs/core";
import { integerVF, moneyVF } from "./AgTable/gridOptions";
import { StatsStore, useStatsData } from "./stats-store";

const moment = require("moment-timezone");

const { RangePicker } = DatePicker;

export function PlannerStatsPage() {
  return <PlannerStats initialData={{}} showControlPanel={true} />;
}

export function PlannerStats({ initialData, showControlPanel }) {
  const appMode = useAppMode();
  initialData = useMemo(() => ({ ...initialData, appMode }), [
    initialData,
    appMode,
  ]);
  return (
    <StatsStore.Provider initialData={initialData}>
      <PlannerStatsContent showControlPanel={showControlPanel} />
    </StatsStore.Provider>
  );
}

function PlannerStatsContent({ showControlPanel }) {
  const { loading, data } = useStatsData();

  return loading ? (
    <Spinner />
  ) : (
    <PlannerStatsContentInner data={data} showControlPanel={showControlPanel} />
  );
}
function PlannerStatsContentInner({ data, showControlPanel }) {
  const fullScreen = useFullScreenModeEnabled();
  return (
    <div
      className="planner-stats-wrapper"
      css={`
        width: 100%;
        height: 100%;
      `}
    >
      <PlatformStatsSummaryCallout data={data.platformStats} />
      {!fullScreen && showControlPanel && <ControlPanel />}
      <PlannerStatsTable
        rowData={data.plannerStats.rowData}
        colDefs={data.plannerStats.colDefs}
      />
    </div>
  );
}

function useStartTimestamp() {
  return StatsStore.useStoreState((s) => s.query.start_timestamp);
}
function useEndTimestamp() {
  return StatsStore.useStoreState((s) => s.query.end_timestamp);
}

function ControlPanel() {
  const start = useStartTimestamp();
  const end = useEndTimestamp();
  const myTz = useMeData().timezone;
  const rangePickerValue = [start && start.tz(myTz), end && end.tz(myTz)];
  const appMode = useAppMode();

  const onRangePickerChange = StatsStore.useStoreActions(
    (a) => a.query.rangePickerChanged
  );

  const handleFetchPlannerStatsData = StatsStore.useStoreActions(
    (a) => a.plannerStatsTable.handleFetchData
  );
  const handleFetchPlatformStatsData = StatsStore.useStoreActions(
    (a) => a.platformStats.handleFetchData
  );
  const onSubmit = async (ev) => {
    ev.preventDefault();
    await Promise.all([
      handleFetchPlannerStatsData(appMode),
      handleFetchPlatformStatsData(appMode),
    ]);
  };

  return (
    <Form
      layout="inline"
      onSubmit={onSubmit}
      css={`
        &&& {
          margin-bottom: 16px;
        }
        label {
          color: white !important;
        }
        .ant-calendar-picker {
          width: 550px !important;
        }
      `}
    >
      <Form.Item label="Action Timestamp Range">
        <RangePicker
          onChange={onRangePickerChange}
          format={"ddd ll LT z"}
          value={rangePickerValue}
          showTime
          ranges={{
            Today: () => [moment().startOf("day"), moment()],
            Yesterday: () => [
              moment().startOf("day").subtract(8, "hours").startOf("day"),
              moment().startOf("day").subtract(8, "hours").endOf("day"),
            ],
            "Past 24 Hours": () => [moment().subtract(24, "hours"), moment()],
            "Past 48 Hours": () => [moment().subtract(48, "hours"), moment()],
            "Past 7 Days": () => [moment().subtract(7, "days"), moment()],
            "Past 14 Days": () => [moment().subtract(14, "days"), moment()],
            "All Time": () => ["", ""],
          }}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Submit
        </Button>
      </Form.Item>
    </Form>
  );
}

function PlannerStatsTable({ rowData, colDefs, height = "600px" }) {
  return (
    <AgTable
      rowData={rowData}
      columnDefs={colDefs}
      height={height}
      tableName="plannerStats"
    />
  );
}

function PlatformStatsSummaryCallout({ data }) {
  const calloutText =
    `${integerVF({
      value: data.n_tickets_booked,
    })} tickets have been rebooked across ${integerVF({
      value: data.n_trips_booked,
    })} trips, ` +
    `resulting in ${moneyVF({
      value: data.sum_total_ca_savings,
    })} in savings against held tickets, ` +
    `or ${moneyVF({
      value: data.sum_total_target_savings,
    })} in savings against the target fare price.`;

  const title = useCalloutTitle();
  const fullScreen = useFullScreenModeEnabled();

  return fullScreen ? null : (
    <Callout
      intent={null}
      title={title}
      css={`
        margin-bottom: 48px;
      `}
    >
      {calloutText}
    </Callout>
  );
}

const TITLE_IF_RANGE_SELECTED = "During the time range selected below...";
const TITLE_IF_ALLTIME = "High-Level Summary";

function useCalloutTitle() {
  const start = useStartTimestamp();
  const end = useEndTimestamp();
  return start || end ? TITLE_IF_RANGE_SELECTED : TITLE_IF_ALLTIME;
}
// function dayAfterTomorrow() {
//   let ret = new Date();
//   ret.setDate(new Date().getDate() + 2);
//   ret = ret.toISOString();
//   ret = ret.slice(0, ret.length - 1);
//   return ret;
// }
